import { createBrowserRouter, Navigate } from 'react-router-dom'
import { ROUTES } from '@/config/routes'

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react'
import { Spinner } from '@/components/ui'
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'

// Layouts
const PublicLayout = lazy(() => import('@/components/layout/PublicLayout'))
const AppLayout = lazy(() => import('@/components/layout/Layout'))

// Auth pages
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const SignupPage = lazy(() => import('@/features/auth/pages/SignupPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'))
const AcceptInvitePage = lazy(() => import('@/features/auth/pages/AcceptInvitePage'))
const DemoPickerPage = lazy(() => import('@/features/auth/pages/DemoPickerPage'))
const DemoAutoLoginPage = lazy(() => import('@/features/auth/pages/DemoAutoLoginPage'))

// App pages
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'))

// Generic Analytics Page (DataModel-driven) - handles ALL analytics routes
const GenericFactPage = lazy(() => import('@/features/analytics/pages/GenericFactPage'))

// Dedicated Analytics Pages
const GeographyPage = lazy(() => import('@/features/analytics/pages/GeographyPage'))
const CustomersPage = lazy(() => import('@/features/analytics/pages/CustomersPage'))
const BrandsPage = lazy(() => import('@/features/analytics/pages/BrandsPage'))
const CategoriesPage = lazy(() => import('@/features/analytics/pages/CategoriesPage'))
const ProductsPage = lazy(() => import('@/features/analytics/pages/ProductsPage'))
const ChannelsPage = lazy(() => import('@/features/analytics/pages/ChannelsPage'))
const RetentionPage = lazy(() => import('@/features/analytics/pages/RetentionPage'))
const InventoryPage = lazy(() => import('@/features/analytics/pages/InventoryPage'))
const SupplierOrdersPage = lazy(() => import('@/features/analytics/pages/SupplierOrdersPage'))
const OrdersPage = lazy(() => import('@/features/analytics/pages/OrdersPage'))
const FulfillmentPage = lazy(() => import('@/features/analytics/pages/FulfillmentPage'))
const PnlPage = lazy(() => import('@/features/analytics/pages/PnlPage'))
const SeasonalityPage = lazy(() => import('@/features/analytics/pages/SeasonalityPage'))
const SmartReorderPage = lazy(() => import('@/features/analytics/pages/SmartReorderPage'))
const CustomersMapPage = lazy(() => import('@/features/analytics/pages/CustomersMapPage'))
const ResourceDetailPage = lazy(() => import('@/features/analytics/pages/ResourceDetailPage'))
const CatalogPage = lazy(() => import('@/features/analytics/pages/CatalogPage'))

// Upload page
const UploadPage = lazy(() => import('@/features/upload/pages/UploadPage'))

// Settings pages
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'))
const GeneralSettingsPage = lazy(() => import('@/features/settings/pages/GeneralSettingsPage'))
const ProfileSettingsPage = lazy(() => import('@/features/settings/pages/ProfileSettingsPage'))
const BillingSettingsPage = lazy(() => import('@/features/settings/pages/BillingSettingsPage'))
const TeamSettingsPage = lazy(() => import('@/features/settings/pages/TeamSettingsPage'))
const ConnectorsSettingsPage = lazy(() => import('@/features/settings/pages/ConnectorsSettingsPage'))
const JobsSettingsPage = lazy(() => import('@/features/settings/pages/JobsSettingsPage'))

// Onboarding
const OnboardingPage = lazy(() => import('@/features/onboarding/pages/OnboardingPage'))

// Admin (super_admin god mode)
const AdminLayout = lazy(() =>
  import('@/features/admin/components/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const RequireSuperAdmin = lazy(() =>
  import('@/features/admin/components/RequireSuperAdmin').then((m) => ({
    default: m.RequireSuperAdmin,
  })),
)
const AdminOverviewPage = lazy(() => import('@/features/admin/pages/AdminOverviewPage'))
const TenantsAdminPage = lazy(() => import('@/features/admin/pages/TenantsAdminPage'))
const AuditLogPage = lazy(() => import('@/features/admin/pages/AuditLogPage'))
const PlansAdminPage = lazy(() => import('@/features/admin/pages/PlansAdminPage'))
const MigrationsPage = lazy(() => import('@/features/admin/pages/MigrationsPage'))
const BroadcastPage = lazy(() => import('@/features/admin/pages/BroadcastPage'))
const BulkPlanChangePage = lazy(() => import('@/features/admin/pages/BulkPlanChangePage'))
const AdminPlaceholderPage = lazy(() =>
  import('@/features/admin/pages/PlaceholderPage').then((m) => ({ default: m.PlaceholderPage })),
)

// Error pages
const NotFoundPage = lazy(() => import('@/features/public/pages/NotFoundPage'))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

// Wrap lazy components with Suspense
function LazyPage({ component: Component }: { component: React.ComponentType }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  )
}

/**
 * Route Architecture:
 *
 * - `/app/analytics/:factId` -> GenericFactPage: handles all DataModel-driven facts
 *   that follow the standard KPIs + trend + breakdown pattern.
 *
 * - Dedicated routes (products, brands, categories, customers, channels, orders, etc.)
 *   exist for pages with unique features beyond GenericFactPage's capabilities:
 *   - Products: ABC classification, multi-series trends
 *   - Brands: concentration analysis, portfolio health
 *   - Categories: macro/micro level toggle
 *   - Customers: B2B/B2C filter, RFM segments
 *   - Channels: custom API hooks, revenue mix
 *   - Orders: pipeline states, hourly distribution, coupon analysis
 *
 * GenericFactPage serves all template-specific facts (SaaS, Restaurant, etc.)
 * while dedicated pages serve the ecommerce-specific analytics.
 */
export const router = createBrowserRouter([
  // Root redirects to login
  {
    path: '/',
    element: <Navigate to={ROUTES.LOGIN} replace />,
  },

  // Demo mode entry — picker lists all demo scenarios
  {
    path: '/demo',
    element: <LazyPage component={DemoPickerPage} />,
  },
  // Per-scenario auto-login (e.g. /demo/ecommerce)
  {
    path: '/demo/:slug',
    element: <LazyPage component={DemoAutoLoginPage} />,
  },

  // Auth routes (no layout wrapper needed)
  {
    path: ROUTES.LOGIN,
    element: <LazyPage component={LoginPage} />,
  },
  {
    path: ROUTES.SIGNUP,
    element: <LazyPage component={SignupPage} />,
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <LazyPage component={ForgotPasswordPage} />,
  },
  {
    path: ROUTES.ACCEPT_INVITE,
    element: <LazyPage component={AcceptInvitePage} />,
  },

  // Onboarding (protected but no sidebar)
  {
    path: ROUTES.ONBOARDING,
    element: (
      <ProtectedRoute>
        <LazyPage component={OnboardingPage} />
      </ProtectedRoute>
    ),
  },

  // Protected routes with app layout
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <AppLayout />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      // Dashboard
      {
        index: true,
        element: <LazyPage component={DashboardPage} />,
      },

      // Generic Analytics Route (DataModel-driven)
      // Handles ALL analytics pages: /app/analytics/:factId
      // Examples: /app/analytics/mrr_daily, /app/analytics/occupancy_daily
      {
        path: 'analytics/:factId',
        element: <LazyPage component={GenericFactPage} />,
      },

      // Geography Analytics
      {
        path: 'geography',
        element: <LazyPage component={GeographyPage} />,
      },

      // Customers Analytics
      {
        path: 'customers',
        element: <LazyPage component={CustomersPage} />,
      },

      // Brands Analytics
      {
        path: 'brands',
        element: <LazyPage component={BrandsPage} />,
      },

      // Categories Analytics
      {
        path: 'categories',
        element: <LazyPage component={CategoriesPage} />,
      },

      // Products Analytics
      {
        path: 'products',
        element: <LazyPage component={ProductsPage} />,
      },

      // Channels Analytics
      {
        path: 'channels',
        element: <LazyPage component={ChannelsPage} />,
      },

      // Retention & Cohort Analytics
      {
        path: 'retention',
        element: <LazyPage component={RetentionPage} />,
      },

      // Inventory Analytics
      {
        path: 'inventory',
        element: <LazyPage component={InventoryPage} />,
      },

      // Supplier Orders Analytics
      {
        path: 'supplier-orders',
        element: <LazyPage component={SupplierOrdersPage} />,
      },

      // Smart Reorder
      {
        path: 'smart-reorder',
        element: <LazyPage component={SmartReorderPage} />,
      },

      // Orders Analytics
      {
        path: 'orders',
        element: <LazyPage component={OrdersPage} />,
      },

      // Fulfillment Analytics
      {
        path: 'fulfillment',
        element: <LazyPage component={FulfillmentPage} />,
      },

      // P&L Analytics
      {
        path: 'pnl',
        element: <LazyPage component={PnlPage} />,
      },

      // Seasonality Analytics
      {
        path: 'seasonality',
        element: <LazyPage component={SeasonalityPage} />,
      },

      // Generic Catalog (paginated browse)
      {
        path: 'catalog/:type',
        element: <LazyPage component={CatalogPage} />,
      },

      // Resource Detail (drill-down) pages
      {
        path: 'brands/:value',
        element: <LazyPage component={ResourceDetailPage} />,
      },
      {
        path: 'categories/:value',
        element: <LazyPage component={ResourceDetailPage} />,
      },
      {
        path: 'products/:value',
        element: <LazyPage component={ResourceDetailPage} />,
      },
      {
        path: 'geography/:value',
        element: <LazyPage component={ResourceDetailPage} />,
      },
      {
        path: 'suppliers/:value',
        element: <LazyPage component={ResourceDetailPage} />,
      },
      {
        path: 'customer/:value',
        element: <LazyPage component={ResourceDetailPage} />,
      },

      // Customers Map (Sales Agent)
      {
        path: 'customers-map',
        element: <LazyPage component={CustomersMapPage} />,
      },

      // Upload (Sales Agent)
      {
        path: 'upload',
        element: <LazyPage component={UploadPage} />,
      },

      // Settings (with nested routes)
      {
        path: 'settings',
        element: <LazyPage component={SettingsPage} />,
        children: [
          {
            index: true,
            element: <LazyPage component={GeneralSettingsPage} />,
          },
          {
            path: 'general',
            element: <LazyPage component={GeneralSettingsPage} />,
          },
          {
            path: 'profile',
            element: <LazyPage component={ProfileSettingsPage} />,
          },
          {
            path: 'billing',
            element: <LazyPage component={BillingSettingsPage} />,
          },
          {
            path: 'team',
            element: <LazyPage component={TeamSettingsPage} />,
          },
          {
            path: 'connectors',
            element: <LazyPage component={ConnectorsSettingsPage} />,
          },
          {
            path: 'jobs',
            element: <LazyPage component={JobsSettingsPage} />,
          },
        ],
      },
    ],
  },

  // Admin (super_admin god mode)
  // Feature-flagged: only active when VITE_ADMIN_UI=true (see RequireSuperAdmin)
  {
    path: '/admin',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RequireSuperAdmin>
          <AdminLayout />
        </RequireSuperAdmin>
      </Suspense>
    ),
    children: [
      { index: true, element: <LazyPage component={AdminOverviewPage} /> },
      {
        path: 'tenants',
        element: <LazyPage component={TenantsAdminPage} />,
      },
      {
        path: 'migrations',
        element: <LazyPage component={MigrationsPage} />,
      },
      {
        path: 'plans',
        element: <LazyPage component={PlansAdminPage} />,
      },
      {
        path: 'plans/bulk-change',
        element: <LazyPage component={BulkPlanChangePage} />,
      },
      {
        path: 'audit',
        element: <LazyPage component={AuditLogPage} />,
      },
      {
        path: 'broadcast',
        element: <LazyPage component={BroadcastPage} />,
      },
      {
        path: 'impersonate',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AdminPlaceholderPage
              title="Bulk Impersonation"
              phase="Fase 5 — use the per-tenant Impersonate button on /admin/tenants"
            />
          </Suspense>
        ),
      },
    ],
  },

  // 404 page
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicLayout />
      </Suspense>
    ),
    children: [
      {
        path: '*',
        element: <LazyPage component={NotFoundPage} />,
      },
    ],
  },
])
